import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "../components/ui/card";
import { CheckCircle2, Loader2, MessageSquare, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ClientPortal — the client's private landing page at `/portal/:clientId`.
 * Shows their latest bureau scores + lets them send a question directly
 * to the owner (fires the same POST /api/public/client-question that the
 * public /client-update page uses, so all questions land in one owner
 * inbox regardless of entry point).
 */
export default function ClientPortal() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Question form state (subject + message).  Email is auto-taken from
  // the client record so they never have to re-enter it.
  const [q, setQ] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/clients/${clientId}`)
      .then((res) => setClient(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientId]);

  const sendQuestion = async () => {
    if (!q.subject.trim() || !q.message.trim()) {
      setError("Please enter a subject and message.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const [firstName, ...rest] = (client?.name || "Client").trim().split(/\s+/);
      const lastName = rest.join(" ") || "Client";
      await axios.post(`${API}/public/client-question`, {
        firstName,
        lastName,
        email: client?.email || "",
        clientId: client?.id || clientId,
        subject: q.subject.trim(),
        message: q.message.trim(),
      }, { timeout: 20000 });
      setSent(true);
      setQ({ subject: "", message: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
      </div>
    );
  }
  if (!client) return <div className="text-center py-12 text-slate-400">Client not found</div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6 md:p-10">
      <div className="max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="portal-welcome">
            Welcome, {client.name}
          </h1>
          <p className="text-zinc-400 mt-1">Your credit repair progress</p>
        </div>

        {/* Bureau score tiles */}
        <div className="grid md:grid-cols-3 gap-4">
          {['equifax', 'experian', 'transunion'].map((bureau) => (
            <Card key={bureau} className="bg-zinc-900 border-zinc-800" data-testid={`portal-score-${bureau}`}>
              <CardHeader>
                <CardTitle className="capitalize">{bureau}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-center">
                  {client[`current_score_${bureau}`] || client[`score_${bureau}`] || '—'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Ask a Question */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-cyan-400/25 rounded-2xl p-6 md:p-8 shadow-2xl"
          data-testid="portal-question-block"
        >
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Ask me a question</h2>
              <p className="text-[13px] text-slate-400 mt-0.5 leading-relaxed">
                I&apos;ll get your message in my inbox instantly and reply to
                {client?.email ? <> your email <span className="text-cyan-300 font-medium">{client.email}</span></> : <> the email I have on file</>}.
              </p>
            </div>
          </div>

          {sent ? (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-4 text-emerald-200 text-sm flex items-start gap-2" data-testid="portal-question-sent">
              <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Question sent!</div>
                <div className="text-[12px] text-emerald-100/80 mt-0.5">Watch your email — I&apos;ll usually reply within a business day.</div>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-2 text-[12px] underline text-cyan-300 hover:text-cyan-200"
                  data-testid="portal-question-send-another"
                >
                  Send another question
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Subject *</label>
                <input
                  type="text"
                  value={q.subject}
                  onChange={(e) => setQ({ ...q, subject: e.target.value })}
                  placeholder="e.g., Quick question about my next round"
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  data-testid="portal-question-subject"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Message *</label>
                <textarea
                  value={q.message}
                  onChange={(e) => setQ({ ...q, message: e.target.value })}
                  rows={5}
                  placeholder="What's on your mind?"
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  data-testid="portal-question-message"
                  maxLength={5000}
                />
                <div className="text-[10px] text-slate-500 text-right mt-1">{q.message.length} / 5000</div>
              </div>
              {error && (
                <div className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5" data-testid="portal-question-error">{error}</div>
              )}
              <button
                type="button"
                onClick={sendQuestion}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all shadow-lg shadow-cyan-500/20"
                data-testid="portal-question-submit"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4" /> Send Question</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
